import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { ValidateParamsService } from '../../../../core/services/validate-params.service';
import { CampaignStatus } from '../../../../core/enum/campaign-status.enum';
import { InvestmentFilterType } from '../../../../core/enum/investment-filter-type.enum';
import { TaxIncentivesEligibility } from '../../../../core/enum/tax-incentives-eligibility.enum';
import { Campaign } from '../../../../core/models/campaign/campaign';
import { CampaignService } from '../../../../core/services/api/campaign.service';
import { SettingService } from '../../../../core/services/api/setting.service';
import { ErrorService } from '../../../../core/services/error.service';
import { QueryParamsService } from '../../../../core/services/query-params.service';
import { CampaignSectionFilter } from '../../campaign-section/campaign-section-filter';

@Component({
    selector: 'app-success-campaigns',
    templateUrl: './success-campaigns.component.html',
    styleUrls: ['./success-campaigns.component.scss'],
    standalone: false
})
export class SuccessCampaignsComponent implements OnInit {
  campaigns: Campaign[];
  params: any;
  total: number = 0;
  isLoading: boolean = false;
  allowedParams: string[] = [
    ...Object.keys(new CampaignSectionFilter()),
    'partnerIds',
    'partnerSlugs',
    'sectorIds',
  ];
  validationSchema = {
    campaignStatus: {
      allowed: [CampaignStatus.Success],
      defaultValue: CampaignStatus.Success,
    },
    landingPageAngelTaxIncentiveEligibility: {
      allowed: Object.values(TaxIncentivesEligibility),
      defaultValue: 0,
    },
    orderBy: {
      allowed: Object.values(InvestmentFilterType),
      defaultValue: 0,
    },
  };

  constructor(
    private campaignService: CampaignService,
    private settingService: SettingService,
    private errorService: ErrorService,
    private queryParamsService: QueryParamsService,
    private validateParamsService: ValidateParamsService,
    @Inject(PLATFORM_ID) private platform: Object,
  ) {}

  ngOnInit(): void {
    this.getInitialList();
  }

  getInitialList() {
    // Will load the list based on the query string on page load
    let defaultParams = new CampaignSectionFilter(CampaignStatus.Success, 30);

    this.params = this.validateParamsService.sanitize(
      this.validationSchema,
      {
        ...defaultParams,
        ...this.queryParamsService.getParams(),
      },
      this.allowedParams,
    );

    // Reserve this function for future
    // this.getSectors(this.queryParamsService.getParam("sectorIds"));
  }

  getList(params: any) {
    this.isLoading = true;
    this.params = params;
    this.campaigns = [];

    this.campaignService
      .getCampaigns(this.params)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          delete this.params.sectorIds;
        }),
      )
      .subscribe(
        (data) => {
          this.campaigns = data.data;
          this.total = data.total;
        },
        (err) => {
          // As url can be changed freely, some params might be invalid,
          // so once API detects invalid fields, will have to reset the list
          this.errorService.showError(err, ({ error }) => {
            if (
              error?.code == 'ERR_INVALID_CAMPAIGN_STATUS' ||
              error?.code == 'ERR_INVALID_FIELD'
            ) {
              this.resetList();
              return;
            }
          });
        },
      );
  }

  resetList() {
    this.updateUrl(new CampaignSectionFilter(CampaignStatus.Success, 30));
  }

  getSectors(sectorIds) {
    if (sectorIds) {
      this.settingService.getSectorsByIds(sectorIds).subscribe(
        (data) => {
          this.params.selectedSectors = data;
        },
        (err) => {
          this.errorService.showError(err);
        },
      );
    }
  }

  updateUrl(params: any) {
    // will use `sectorIds` for query string instead of `selectedSectors` obj
    // as object is not url friendly
    if (params.selectedSectors) {
      params['sectorIds'] = params.selectedSectors.map((sector) => sector.id || sector.key);
    }
    this.queryParamsService.updateUrl(params, ['take', 'selectedSectors']);
    this.getList(params);
  }
}
